"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.describePorts = describePorts;
exports.contractsOf = contractsOf;
/**
 * SOLID Review
 * S — Strong. One concern: turn a component's port fields into a flat, serialisable manifest.
 *     It reads structure and reports it; it neither assembles nor validates.
 * O — Strong. A new Port subclass is classified by the guards below; consumers of IEntityPort
 *     are unaffected. Adding a descriptor field is additive for every existing consumer.
 * L — N/A — no inheritance hierarchy of its own.
 * I — Strong. Two exported types and one function; a caller that wants a diagram and a caller that
 *     wants an assembly assertion both need exactly this.
 * D — Strong. Depends only on the kernel's own abstractions. It takes `object`, not a component
 *     type, so it has no knowledge of ComponentApplication or ComponentInfrastructure.
 */
const Port_1 = require("./Port");
const PortProvided_1 = require("./PortProvided");
const PortHolder_1 = require("./PortHolder");
function describeContract(contract) {
    return contract === null
        ? null
        : { name: contract.name, digest: contract.digest, local: contract.local, stream: contract.stream };
}
/** Own enumerable fields only — prototype getters are deliberately not walked (see below). */
function isPortEntry(entry) {
    return entry[1] instanceof Port_1.Port;
}
/** A socket is anything that can report whether it is connected. */
function asSocket(port) {
    const p = port;
    return typeof p.isConnected === 'function' ? p : null;
}
/** A defaulting socket additionally reports whether it is running on its fallback. */
function asDefaulting(port) {
    const p = port;
    return typeof p.usingFallback === 'function' && typeof p.isConnected === 'function'
        ? p
        : null;
}
/**
 * A port that can report whether it has been read.
 *
 * CAPABILITY-DETECTED for the same reason as asSocket/asDefaulting below: the two required-port
 * variants have their own package subpaths, so under Vite's pre-bundling a socket can arrive from a
 * different module instance than the one this file imported. `instanceof` is not usable here.
 */
function asReadable(port) {
    const p = port;
    return typeof p.wasRead === 'function' ? p : null;
}
/**
 * Whether a port currently resolves to something.
 *
 * STRUCTURAL, NOT `instanceof` (2026-08-19). This used instanceof and broke the moment the frontend
 * became a consumer: `@ml3d/kernel` and `@ml3d/kernel/required-with-default` resolve to DIFFERENT
 * MODULE INSTANCES under Vite's dependency pre-bundling, so a `RequiredPortWithDefault` built from
 * the subpath is not `instanceof` the class `describe.ts` imported. The failure was loud —
 * "unsupported Port subclass 'RequiredPortWithDefault'" — but only because the previous version
 * threw rather than guessing; a `return false` would have silently reported every frontend port as
 * disconnected and drawn a confident, wrong diagram.
 *
 * The subpaths exist so dependency-cruiser can forbid the wrong variant per workspace (§28.42), so
 * the duplication is a consequence of a deliberate design choice, not an accident to configure
 * away. Capability detection is immune to it: what the manifest needs to know is whether a port
 * CAN report connection state, not which copy of the class it came from.
 *
 * A Port exposing neither capability still throws: reporting an unknown port as "not connected"
 * would put a confident, wrong note on a generated diagram (§28.18).
 */
function isConnected(port) {
    const socket = asSocket(port);
    if (socket !== null)
        return socket.isConnected();
    // ProvidedPort stays an instanceof check and is safe as one: only the two REQUIRED variants have
    // their own package subpaths, so only they can arrive from a second module instance. Everything
    // else — Port, ProvidedPort, Connector — is reachable exclusively through the barrel.
    if (port instanceof PortProvided_1.ProvidedPort)
        return true;
    throw new Error(`describePorts: unsupported Port subclass '${port.constructor.name}' for port '${port.name}'. ` +
        'Teach describePorts how to classify it before using it in a component.');
}
/** One port, flattened. */
function describeOne(componentName, port) {
    return {
        component: componentName,
        name: port.name,
        kind: (asSocket(port) === null ? 'provided' : 'required'),
        connected: isConnected(port),
        defaulted: asDefaulting(port)?.usingFallback() ?? false,
        read: asReadable(port)?.wasRead() ?? false,
        contract: describeContract(port.contract),
    };
}
/**
 * Enumerate the ports a component declares, INCLUDING those it groups into `PortHolder`s.
 *
 * WHAT IT WALKS. `Object.entries` — own enumerable properties. Ports are declared as
 * `readonly xPort = new RequiredPort<T>('X')`, so they are own fields and are found. Accessor
 * properties on the prototype (`get auth()` on ComponentInfrastructure, for instance) are NOT
 * walked, which is correct: those are convenience facades over already-declared ports, and
 * invoking them here would both duplicate entries and run construction logic during a read.
 *
 * ⚠ AND IT DESCENDS INTO `PortHolder` FIELDS, SINCE 2026-09-09. It used to do exactly one pass and
 * stop, so a component that grouped its ports into holders returned `[]` — silently, taking that
 * component out of every manifest and diagram. Six components carried a hand-written
 * `describeOwnPorts()` to work around it, and twenty call sites had to remember to prefer it. The
 * descent is gated on the BRAND rather than on "is an object", because blind recursion measurably
 * reports the wrong thing: see PortHolder.ts for the ComponentApplication/ComponentDomain case.
 *
 * DEPTH IS NOT CAPPED AT ONE. A holder that groups holders is walked all the way down, because
 * there is no reading of the model on which a port two holders deep belongs to a different
 * component than one holder deep. `seen` guards a cycle rather than a depth: a holder reachable
 * from itself is described once, not forever.
 *
 * ORDER. Declaration order, depth-first — the order a reader of the component sees, and identical
 * to what the six `describeOwnPorts()` methods produced, all of which listed required ports before
 * provided ones exactly as the fields are declared. Consumers that need byte-stability across
 * refactors sort explicitly rather than relying on this.
 *
 * @param componentName the label to attribute these ports to; the caller names the component
 *                      because a class name is a build artefact and the diagram wants the model name
 */
function describePorts(componentName, component) {
    return walk(component, new Set()).map((port) => describeOne(componentName, port));
}
/**
 * The contracts a component's ports carry, keyed by port name — the SAME walk as `describePorts`,
 * projected differently. `describePorts` flattens to a serialisable manifest and so carries only
 * the contract's name and digest; the generator that writes `port-contracts.json` needs the
 * contract object itself to render each op's schema, and gets it here. Ports without a contract
 * are absent from the map, not present as `null`: the generator lists them from `describePorts`.
 *
 * ⚠ A NAME DECLARED TWICE ON ONE COMPONENT WITH TWO DIFFERENT CONTRACTS IS REFUSED. Nothing in the
 * model allows it — a port name is an identity — and a map that silently kept the last one would
 * render a wrong artifact with every gate green.
 */
function contractsOf(component) {
    const out = new Map();
    for (const port of walk(component, new Set())) {
        if (port.contract === null)
            continue;
        const existing = out.get(port.name);
        if (existing !== undefined && existing.digest !== port.contract.digest) {
            throw new Error(`contractsOf: port '${port.name}' is declared twice on one component with different contracts ` +
                `(${existing.digest} and ${port.contract.digest}).`);
        }
        out.set(port.name, port.contract);
    }
    return out;
}
function walk(node, seen) {
    if (seen.has(node))
        return [];
    seen.add(node);
    const ports = [];
    for (const entry of Object.entries(node)) {
        if (isPortEntry(entry)) {
            ports.push(entry[1]);
        }
        else if ((0, PortHolder_1.isPortHolder)(entry[1])) {
            ports.push(...walk(entry[1], seen));
        }
    }
    return ports;
}
//# sourceMappingURL=describe.js.map